import { orderedStudioScenarioTree } from 'src/components/star-grid/utils/studioScenarioOrder';
import type { StudioBinding } from 'src/components/star-grid/reportStudio.types';
import type { PnlHierarchyTree } from 'src/components/star-grid/starGridTypes';
import { getHierarchyTree } from 'src/components/star-grid/services/starGridService';
import { apiRequest } from 'src/components/star-grid/services/starGridHttp';
import { ReportDefinitionApiError } from 'src/components/star-grid/utils/reportDefinitionError';
import {
  parseStudioHierarchyWeights,
  studioTreeFromWeights,
  parseStudioHierarchyMembers,
  studioTreeFromMembers
} from 'src/components/star-grid/utils/studioHierarchyWeights';
import { PNL_DIMENSIONS_PATH } from 'src/components/star-grid/reportStudioConstants';

const rollupPath = (binding: StudioBinding, leaf: string): string => {
  const { dimension, hierarchy } = binding.rollup!;
  return `${PNL_DIMENSIONS_PATH}/${encodeURIComponent(dimension)}/hierarchies/${encodeURIComponent(hierarchy)}/${leaf}`;
};

/** An already-nested member tree (`[{id,label,is_leaf,children}]`): a rollup's members, or a flat binding's own list. */
const loadMembers = async (path: string, binding: StudioBinding, signal: AbortSignal): Promise<PnlHierarchyTree | undefined> => {
  const members = await parseStudioHierarchyMembers(await apiRequest(path, undefined, signal));
  return members.length ? studioTreeFromMembers(binding, members) : undefined;
};

const loadHierarchyTree = async (binding: StudioBinding, signal: AbortSignal): Promise<PnlHierarchyTree | undefined> => {
  if (binding.tree_id) return getHierarchyTree(binding.tree_id, signal);
  if (!binding.rollup) return binding.members_path ? loadMembers(binding.members_path, binding, signal) : undefined;
  // A rollup binding: prefer the weighted closure (correct subtotal signs) when it covers the
  // hierarchy; fall back to the rollup member tree (structure only) for hierarchies the weighted
  // closure lacks — e.g. FPA Deck Line Item Opex, DaS Product Line. Both 404 -> genuinely no members.
  try {
    const weights = await parseStudioHierarchyWeights(await apiRequest(rollupPath(binding, 'weights'), undefined, signal));
    if (weights.length) return studioTreeFromWeights(binding, weights);
  } catch (error) {
    if (!(error instanceof ReportDefinitionApiError && error.status === 404)) throw error;
  }
  try {
    return await loadMembers(rollupPath(binding, 'members'), binding, signal);
  } catch (error) {
    if (error instanceof ReportDefinitionApiError && error.status === 404) return undefined;
    throw error;
  }
};

export const loadStudioHierarchyTree = async (binding: StudioBinding, signal: AbortSignal): Promise<PnlHierarchyTree | undefined> =>
  orderedStudioScenarioTree(await loadHierarchyTree(binding, signal), binding.dimension);
